import React, { useEffect, type Dispatch } from 'react';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import i18next from 'i18next';
import { useDarkMode } from './DarkModeContext';

import Header from './components/Layout/Header/Header';
import Ribbon from './components/Layout/Ribbon/Ribbon';

import ErrorBoundary, { type IFallbackComponentProps } from './components/ErrorBoundary';
import availabilityConfig from './config/availabilityConfig.json';
import keyboardShortcuts from './config/keyboardShortcuts.json';
import { GlobalVariables } from './pdf/GlobalVariables';

import { useDispatch, useSelector } from 'react-redux';
import { ReduxUpdater } from './pdf/ReduxUpdater';
import { GlobalDialogs } from './pdf/GlobalDialogs';

import MainContent from './components/Layout/MainContent';
import ShowDialog from './components/Layout/Dialogs/ShowDialog';
import ShowMessage from './components/Layout/Dialogs/ShowMessage';
import ShowStampDialog from './components/Layout/Dialogs/ShowStampDialog';

import styles from './App.module.scss';
import { canEditAnyAnnot, numberFromHex } from './utils';
import { PdfViewerViewModeFlag } from './pdf/Enums';
import { type IGlobalStore } from './Redux/ReduxStore';
import { ApryseUpdater } from './pdf/ApryseUpdater';
import { ApryseSDK } from './pdf/ApryseSDK';
import AlertModalDialog from './components/Layout/Dialogs/AlertModalDialog';
import ShowContextMenu from './components/Layout/Dialogs/ShowContextMenu';
import Loading from './components/General/Loading/Loading';
import { getInitSettings } from './Redux/InitSettingsSlice';
import { getUserProfile } from './Redux/UserSettingsSlice';
import { RedliningActionLogger } from './pdf/RedliningActionLogger';
import { useEnqueueSnackbar } from './hooks/useEnqueueSnackbar';
import { ToastSeverity } from './components/General/Toast/Toast';
import SessionTimeout from './components/General/SessionTimeout/SessionTimeout';
import { GlobalConstants } from './pdf';

export const backendApiUrl = {
    init: 'api/init',
    document: {
        history: 'api/document/history',
        info: 'api/document/info',
        register: 'api/document/register',
    },
    user: {
        logout: 'api/user/logout',
        profile: 'api/user/profile',
        profileImage: 'api/user/profile/image',
        profileProtection: 'api/user/profile/protection',
        settings: 'api/user/settings',
        theme: 'api/user/settings/theme',
        language: 'api/user/language',
    },
    pdf: {
        open: 'api/pdf/{documentId}/open',
        openlocal: 'api/pdf/open/local/{documentUrl}',
        info: 'api/pdf/info/{documentId}',
        save: 'api/pdf/save',
    },
    stamps: {
        all: 'api/stamps',
        save: 'api/stamps/save',
        delete: 'api/stamps/delete',
        image: 'api/stamps/image/{id}',
    },
    session: {
        check: 'api/session/check',
        update: 'api/session/update',
    },
    log: {
        redlinings: 'api/log/redlinings'
    }
};

/**
 * The error fallback component.
 */
const ErrorFallbackComponent: React.FunctionComponent<IFallbackComponentProps> = props => {
    const { error, errorInfo } = props;

    return (
        <Card>
            <CardContent>
                <Typography gutterBottom variant="h5" component="div">
                    {`${error.message || i18next.t('errors.unknown')}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {errorInfo.componentStack}
                </Typography>
            </CardContent>
        </Card>
    );
};

let pdfDocument = process.env.REACT_APP_DOC_URL || '';
let pdfDocument2 = '';

// Parse document from URL
if (window.location.pathname?.length > 1) {
    pdfDocument = window.location.pathname;
    const parts = window.location.pathname.split('/');
    if (parts.length === 3) {
        pdfDocument = parts[1];
        pdfDocument2 = parts[2];
    }
}

const documentSuffix = window.location.pathname?.substring(window.location.pathname.length - 4);
if (
    [...GlobalConstants.supported.documents, ...GlobalConstants.supported.images].includes(
        documentSuffix,
    )
) {
    pdfDocument = '/docs' + window.location.pathname;
    const parts = window.location.pathname.split('/');
    if (parts.length === 3) {
        pdfDocument = '/docs/' + parts[1];
        pdfDocument2 = '/docs/' + parts[2];
    }
}

const params = new URL(document.location.toString()).searchParams;
if (params) {
    const newColor = params.get('new');
    if (newColor) {
        ReduxUpdater.compareColorAdded = numberFromHex(newColor);
    }
    const oldColor = params.get('old');
    if (oldColor) {
        ReduxUpdater.compareColorRemoved = numberFromHex(oldColor);
    }
    const mode = params.get('m');
    if (mode) {
        ReduxUpdater.compareMode = parseInt(mode);
    }
}

export const handleDeleteKey = () => {
    if (
        !ApryseSDK.selectedAnnotations?.length ||
        ApryseSDK.textAnnotationEditingContext ||
        GlobalVariables.disableKeyboardShortcuts
    ) {
        return;
    }

    if (!canEditAnyAnnot(ApryseUpdater.selectedAnnotations)) {
        GlobalDialogs.showNoty(i18next.t('deleteAnnotDialog.subtextNoPerm'), ToastSeverity.Error);
    } else {
        GlobalDialogs.showDialog(
            i18next.t('deleteAnnotDialog.title', {
                count: ApryseUpdater.selectedAnnotations.length,
            }),
            <AlertModalDialog
                dialogTitle={i18next.t('deleteAnnotDialog.title', {
                    count: ApryseUpdater.selectedAnnotations.length,
                })}
                dialogBody={i18next.t('deleteAnnotDialog.subtext', {
                    count: ApryseUpdater.selectedAnnotations.length,
                })}
                cancelText={i18next.t('deleteAnnotDialog.cancelButtonText')}
                confirmText={i18next.t('deleteAnnotDialog.confirmButtonText')}
                isActionDisabled={false}
                isOpen={true}
                onCancel={GlobalDialogs.closeDialog}
                onConfirm={() => {
                    if (ApryseUpdater.selectedAnnotations.length === 0) {
                        return;
                    }
                    ApryseSDK.deleteSelectedAnnotations();
                    GlobalDialogs.closeDialog();
                }}
                onClose={GlobalDialogs.closeDialog}
            />,
        );
    }
};

const handleCtrlKeyCombos = (e: KeyboardEvent): boolean => {
    if (!e.ctrlKey || e.altKey) {
        return false;
    }

    switch (e.key.toLowerCase()) {
        case 'a':
            e.preventDefault();
            ApryseSDK.commonSelectAll();
            break;
        case 'c':
            e.preventDefault();
            e.stopPropagation();
            ApryseSDK.commonCopy();
            break;
        case 'f':
            e.preventDefault();
            e.stopPropagation();
            ReduxUpdater.selectedSidepanel = 'search';
            break;
        case 'v':
            e.preventDefault();
            e.stopPropagation();
            ApryseSDK.commonPaste();
            break;
        case 'x':
            // ToDO: when copy/paste buttons are implemented
            // GlobalDialogs.showNoty(i18next.t('notifications.textCutToClipboard'), 'success');
            break;
        case 'y':
            e.preventDefault();
            ApryseSDK.redo();
            break;
        case 'z':
            e.preventDefault();
            ApryseSDK.undo();
            break;
        case 'p':
            e.preventDefault();
            // ToDO: GlobalDialogs.showDialog(i18next.t('printDialog.title'), <PrintDialog />);
            break;
        default:
            return false;
    }
    return true;
};

const handleEscapeKey = (e: KeyboardEvent) => {
    if (e.code !== 'Escape') {
        return;
    }

    e.preventDefault();
    e.stopPropagation();
    ApryseSDK.setToolMode(ApryseSDK.ToolsENUM.DEFAULT);
};

const generateShortcutString = (e: KeyboardEvent): string => {
    let shortcutToCheck = e.ctrlKey ? 'Ctrl+' : '';
    if (e.altKey) {
        shortcutToCheck = `${shortcutToCheck}Alt+`;
    }
    if (e.shiftKey) {
        shortcutToCheck = `${shortcutToCheck}Shift+`;
    }
    if (e.key) {
        shortcutToCheck = `${shortcutToCheck}${e.key.toUpperCase()}`;
    }
    return shortcutToCheck;
};

export const onWindowKeyDown = (e: KeyboardEvent) => {
    if (e.code === 'Delete') {
        handleDeleteKey();
    }

    const shortcutToCheck = generateShortcutString(e);

    if (
        GlobalVariables.isTextEditing &&
        !ReduxUpdater.allowWhileTextEditingCommands.includes(
            ReduxUpdater.shortcuts[shortcutToCheck],
        )
    ) {
        return;
    }

    if (handleCtrlKeyCombos(e)) {
        return;
    }

    if (
        Object.keys(ReduxUpdater.shortcuts).includes(shortcutToCheck) &&
        !ReduxUpdater.virtualOnlyCommands.includes(ReduxUpdater.shortcuts[shortcutToCheck]) &&
        !GlobalVariables.disableKeyboardShortcuts
    ) {
        e.preventDefault();
        e.stopPropagation();
        const key = ReduxUpdater.shortcuts[shortcutToCheck];
        if (
            !(
                ReduxUpdater.editmodeOnlyCommands.includes(key) &&
                ReduxUpdater.viewMode === PdfViewerViewModeFlag.View
            )
        ) {
            if (Object.keys(GlobalVariables.registeredCommands).includes(key)) {
                GlobalVariables.registeredCommands[key]();
            } else {
                ApryseSDK.setToolMode(key);
            }
        }
    }

    handleEscapeKey(e);
};

/**
 * The main component of the app.
 */
export default () => {
    /** Allows reducers on a redux slice to be called. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dispatch: Dispatch<any> = useDispatch();
    GlobalDialogs.enqueueSnackbar = useEnqueueSnackbar();

    const { isDark, setDark } = useDarkMode();

    useEffect(() => {
        document.body.classList.toggle('dark-mode', isDark);
    }, [isDark]);

    useEffect(() => {
        dispatch(getInitSettings());
        dispatch(getUserProfile()).then((action) => {
            if (getUserProfile.fulfilled.match(action) && action.payload.theme) {
                setDark(action.payload.theme === 'dark');
            }
        });
    }, []);

    const { lic, testRun, customFontURL, language } = useSelector(
        (_store: IGlobalStore) => _store.initSettings.data,
    );

    const licLoading = useSelector((state: IGlobalStore) => state.initSettings.loading);
    const userLoading = useSelector((state: IGlobalStore) => state.userSettings.loading);

    React.useMemo(() => {
        GlobalVariables.isForTestRun = process.env.REACT_APP_TESTRUN === 'true' || testRun === true;
    }, [testRun]);

    const shortcuts = {};
    const allowWhileTextEditingCommands: string[] = [];
    const editmodeOnlyCommands: string[] = [];
    const virtualOnlyCommands: string[] = [];

    const parseShortcuts = (prefix: string, keyboardShortcuts) => {
        Object.keys(keyboardShortcuts).forEach(key => {
            if (typeof keyboardShortcuts[key] !== 'string') {
                if (keyboardShortcuts[key]['command'] !== undefined) {
                    const command: string = keyboardShortcuts[key]['command'];
                    shortcuts[command] = `${prefix}${key}`;
                    shortcuts[`${prefix}${key}`] = command;
                    if (keyboardShortcuts[key]['editModeOnly'] === true) {
                        editmodeOnlyCommands.push(command);
                    }
                    if (keyboardShortcuts[key]['virtualOnly'] === true) {
                        virtualOnlyCommands.push(command);
                    }
                    if (keyboardShortcuts[key]['allowWhileTextEditing'] === true) {
                        allowWhileTextEditingCommands.push(command);
                    }
                } else {
                    parseShortcuts(`${prefix}${key}+`, keyboardShortcuts[key]);
                }
            }
        });
    };

    const onBeforeUnload = () => {
        if (!ReduxUpdater.documentSaved) {
            RedliningActionLogger.appendLog('close-unsaved');

            return 'Unsaved changes';
        }
    };

    React.useMemo(() => {
        if (pdfDocument2 !== '') {
            ReduxUpdater.setDocumentUrls(pdfDocument, pdfDocument2);
        } else {
            ReduxUpdater.documentURL = pdfDocument;
        }
        ReduxUpdater.lists = availabilityConfig.presets.lists;
        ReduxUpdater.selections = availabilityConfig.presets.selected;
        GlobalVariables.isDefaultCategorySelected = true;

        parseShortcuts('', keyboardShortcuts);
        ReduxUpdater.shortcuts = shortcuts;
        ReduxUpdater.allowWhileTextEditingCommands = allowWhileTextEditingCommands;
        ReduxUpdater.editmodeOnlyCommands = editmodeOnlyCommands;
        ReduxUpdater.virtualOnlyCommands = virtualOnlyCommands;
    }, []);

    React.useEffect(() => {
        window.addEventListener('keydown', onWindowKeyDown);

        if (process.env.REACT_APP_DISPLAY_RELOAD_WARNING?.toLowerCase() !== 'false') {
            window.onbeforeunload = onBeforeUnload;
        }

        return () => {
            window.removeEventListener('keydown', onWindowKeyDown);
        };
    }, []);

    const maincontent = React.useMemo(() => {
        return <MainContent license={lic} customFontURL={customFontURL} language={language} />;
    }, [lic]);

    if (licLoading || userLoading) {
        return <Loading message={i18next.t('loader.loadingPdf')} />;
    }

    return (
        <ErrorBoundary FallbackComponent={ErrorFallbackComponent}>
            <Loading message={i18next.t('loader.loadingPdf')} />
            <Box className={styles.container}>
                <Grid>
                    <Grid container spacing={0}>
                        <Header />
                    </Grid>
                    <Grid container spacing={0}>
                        <Ribbon />
                    </Grid>
                    <Grid
                        container
                        alignItems="stretch"
                        direction="row"
                        justifyContent="flex-start"
                        spacing={0}
                        flexWrap={'nowrap'}
                    >
                        {maincontent}
                    </Grid>
                </Grid>
            </Box>
            <ShowContextMenu />
            <ShowDialog />
            <ShowMessage />
            <ShowStampDialog />
            <SessionTimeout />
            {/* <ShowTooltip /> */}
        </ErrorBoundary>
    );
};
